import { Link } from "react-router-dom";
import Seo from "@/components/Seo";

const EFFECTIVE = "February 26, 2026";

function Section({ n, title, children }) {
  return (
    <section className="border-t border-white/10 py-10 first:border-t-0" data-testid={`refund-section-${n}`}>
      <div className="grid grid-cols-12 gap-6">
        <div className="col-span-12 md:col-span-3">
          <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-[#FFD300]">§ {n.toString().padStart(2, "0")}</div>
          <h2 className="font-heading text-2xl md:text-3xl tracking-tight leading-tight mt-2">{title}</h2>
        </div>
        <div className="col-span-12 md:col-span-9 space-y-4 text-neutral-300 leading-relaxed text-[15px]">
          {children}
        </div>
      </div>
    </section>
  );
}

export default function Refund() {
  return (
    <div data-testid="refund-page">
      <Seo
        title="Refund Policy — Secrecy Executive Training Academy"
        description="Strict refund, cancellation, transfer, and no-show policy for firearms and security training courses at Secrecy Executive Protection LLC. Governed by U.S. federal law and Maryland consumer-protection statutes."
        path="/refund"
      />

      {/* Hero */}
      <section className="border-b border-white/10 pt-24 pb-14">
        <div className="container-x">
          <div className="overline mb-4">// LEGAL 002 · EFFECTIVE {EFFECTIVE.toUpperCase()}</div>
          <h1 className="font-heading text-5xl md:text-7xl tracking-tighter leading-[0.9] max-w-4xl">
            Refund <span className="text-[#FFD300]">Policy.</span>
          </h1>
          <p className="mt-6 text-neutral-400 max-w-2xl text-[15px] leading-relaxed">
            Firearms and security training seats are limited, instructor-scheduled, and irrevocably committed once your
            registration is confirmed. This Policy is a binding contract between you and Secrecy Executive Protection LLC
            ("<strong className="text-white">SEP</strong>"). By registering for a course, you accept every term below.
          </p>
          <div className="mt-6 font-mono text-[10px] tracking-[0.2em] uppercase text-neutral-500">
            Governed by U.S. federal law and the laws of the State of Maryland.
          </div>
        </div>
      </section>

      <div className="container-x py-6">
        {/* Top-line alert */}
        <div className="border-l-4 border-[#FFD300] bg-white/5 px-6 py-5 my-6" data-testid="refund-alert">
          <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-[#FFD300] mb-2">// ALL SALES FINAL POLICY</div>
          <p className="text-white text-[15px] leading-relaxed">
            All course registrations are <strong>final</strong> — including <strong>50% deposit reservations</strong>.
            Refunds are issued <strong>only</strong> under the narrow circumstances defined in §3 below. Everything
            else is handled by <strong>credit toward a future class or transfer to a substitute student</strong> (see §4).
          </p>
        </div>

        {/* Bank statement descriptor callout — helps customers recognize the charge and cite the exact wording in any dispute */}
        <div className="border border-white/15 bg-black/40 px-6 py-5 my-6 grid grid-cols-1 md:grid-cols-3 gap-4 items-center" data-testid="refund-descriptor-callout">
          <div className="md:col-span-1">
            <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-[#FFD300] mb-1">// STATEMENT DESCRIPTOR</div>
            <div className="text-neutral-400 text-xs leading-relaxed">The exact wording that will appear on your bank or card statement:</div>
          </div>
          <div className="md:col-span-2">
            <div className="border border-[#FFD300]/40 bg-[#FFD300]/5 px-4 py-3 font-mono text-lg md:text-xl tracking-[0.15em] text-[#FFD300]" data-testid="refund-descriptor-code">
              SEPROTECTION.COM
            </div>
            <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-neutral-500 mt-2">
              Charges may also appear as <span className="text-neutral-300">SEPROTECTION.COM · 410-645-0722</span> depending on your card network.
            </div>
          </div>
        </div>

        <Section n={1} title="Scope">
          <p>
            This Policy applies to every product and service purchased through seprotection.com or paid to SEP by any other
            means, including but not limited to:
          </p>
          <ul className="list-disc pl-6 space-y-1">
            <li>All firearms training courses (Wear &amp; Carry, CCW, Multi-State, Utah CFP).</li>
            <li>D.C. Armed Special Police Officer — Initial &amp; Renewal courses.</li>
            <li>Maryland Security Guard — Initial &amp; Renewal courses.</li>
            <li>eLearning &amp; digital course access.</li>
            <li>Private instruction, one-on-one range time, and consulting sessions.</li>
            <li>Merchandise, materials, and any add-on purchases.</li>
          </ul>
        </Section>

        <Section n={2} title="Cancellation windows">
          <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#FFD300]">A. Live &amp; in-person courses</p>
          <ul className="list-disc pl-6 space-y-1">
            <li><strong className="text-white">More than 14 days before class:</strong> eligible for a full refund <em>minus</em> a $50 administrative fee <em>or</em> a full course credit valid for 12 months.</li>
            <li><strong className="text-white">7 to 14 days before class:</strong> 50% refund <em>or</em> full credit valid for 12 months.</li>
            <li><strong className="text-white">Less than 7 days before class:</strong> no refund. Course credit at SEP's sole discretion.</li>
            <li><strong className="text-white">Day of class / no-show / late arrival past the safety brief:</strong> no refund, no credit, no re-scheduling.</li>
          </ul>
          <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#FFD300] pt-2">B. eLearning &amp; digital products</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>All digital / eLearning purchases are <strong>final and non-refundable</strong> once access has been granted.</li>
            <li>If a technical defect prevents access, contact us within 7 days for a full refund or replacement.</li>
          </ul>
          <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#FFD300] pt-2">C. Private instruction &amp; consults</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Reschedule at no cost with at least 48 hours' notice.</li>
            <li>Under 48 hours' notice or no-show forfeits the session in full.</li>
          </ul>
          <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#FFD300] pt-2">D. 50% deposit reservations</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>The 50% deposit <strong className="text-white">holds your seat only</strong>. The remaining balance is due <strong>before your class start date</strong>. You may pay the balance any time from your dashboard.</li>
            <li>If the balance is not paid by the class start date, SEP may release your seat without refund of the deposit.</li>
            <li>Deposits are subject to the same cancellation windows (§2A above). A cancellation more than 14 days before class returns the deposit minus the $50 admin fee; 7–14 days before class returns 50%; under 7 days is non-refundable.</li>
          </ul>
        </Section>

        <Section n={3} title="Exceptions where a full refund is issued">
          <p>SEP will issue a <strong className="text-white">full refund (no administrative fee)</strong> only in the following cases:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>SEP <strong>cancels or reschedules</strong> the class and the new date does not work for you.</li>
            <li>The class is <strong>oversubscribed</strong> and we cannot seat you in an equivalent session within 60 days.</li>
            <li>You are found <strong>statutorily ineligible</strong> to take the course at check-in through no fault of SEP (e.g., an issuing agency has changed a rule you could not have known about at registration).</li>
            <li>A <strong>documented medical emergency</strong> (hospital/urgent-care documentation required) or the <strong>death of an immediate family member</strong> prevents attendance and the request is submitted within 14 days of the class date.</li>
            <li>The purchase was made <strong>fraudulently</strong> without your authorization (police report required).</li>
          </ul>
        </Section>

        <Section n={4} title="Transfers & credits">
          <ul className="list-disc pl-6 space-y-1">
            <li>You may <strong className="text-white">transfer your seat</strong> to another qualifying student one time at no charge if requested at least 48 hours before class. A second transfer incurs a $50 administrative fee.</li>
            <li>Course credits are <strong>valid for 12 months</strong> from the original class date and may be applied to any live or eLearning offering of equal or lesser value.</li>
            <li>Credits are <strong>non-refundable, non-transferable to another person, and non-redeemable for cash</strong>.</li>
          </ul>
        </Section>

        <Section n={5} title="How to request a refund or credit">
          <p>All refund and credit requests must be submitted in writing:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li><strong className="text-white">Email:</strong> <a href="mailto:info@seprotection.com" className="link-gold">info@seprotection.com</a> with the subject line "Refund Request — [Order # or Class Date]".</li>
            <li><strong className="text-white">Phone:</strong> <a href="tel:+14106450722" className="link-gold">410-645-0722</a> (voicemail submissions must be followed by a written request).</li>
            <li><strong className="text-white">Required information:</strong> full name on the order, email used at checkout, order/session ID, class date, reason, and any supporting documentation.</li>
          </ul>
          <p>
            Refund decisions are issued in writing within <strong>5 business days</strong> of receipt of a complete request.
            Approved refunds are returned to the original payment method via our payment processor within <strong>10 business days</strong> of
            approval. Bank posting times may add additional days.
          </p>
        </Section>

        <Section n={6} title="Chargebacks & disputes">
          <p>
            <strong className="text-white">Please contact us before initiating a bank or card chargeback.</strong> Almost every refund
            eligible under this Policy can be issued faster and cleaner directly through our payment processor than through a dispute.
          </p>
          <p>
            The charge on your statement will appear as <span className="font-mono text-[#FFD300] tracking-[0.1em]">SEPROTECTION.COM</span>
            {" "}(sometimes shown with our phone number appended, depending on your card network).
            If you do not recognize a charge under that descriptor, email <a href="mailto:info@seprotection.com" className="link-gold">info@seprotection.com</a>
            {" "}with the last 4 digits of the card and the transaction date before filing a dispute — we can resolve it in minutes.
          </p>
          <p>
            Chargebacks filed while a refund request is under review, or filed after refund eligibility has expired,
            constitute a breach of this Policy. SEP reserves the right to (a) contest the chargeback with all supporting
            evidence — including this Policy, your registration timestamp, and the <span className="font-mono text-[#FFD300] tracking-[0.1em]">SEPROTECTION.COM</span> descriptor
            record — (b) suspend your account and cancel any pending enrollments, and (c) recover reasonable
            administrative and legal fees under Maryland law.
          </p>
        </Section>

        <Section n={7} title="Weather, force majeure & range closures">
          <p>
            If SEP is unable to conduct a class due to severe weather, range closure, natural disaster, government order,
            civil disturbance, utility failure, or any other event beyond our reasonable control, we will reschedule the
            affected students to the next available class at no charge. If no suitable rescheduled date can be offered
            within 60 days, a full refund will be issued at your request.
          </p>
        </Section>

        <Section n={8} title="Certification, licensing, and outcomes">
          <p>
            SEP guarantees the delivery of state-certified instruction; SEP does <strong>not</strong> guarantee that any student
            will pass a qualification, receive a certificate, or be granted a permit or commission by any government
            agency (including but not limited to Maryland State Police, D.C. MPD, or Utah BCI). Failure to qualify, failure
            to pass a written exam, or denial by a licensing authority is <strong>not</strong> grounds for a refund.
          </p>
          <p>
            Students who fail a live qualification may re-attempt at the next available class at a reduced re-qualification
            fee published on the course page.
          </p>
        </Section>

        <Section n={9} title="Customer information & communications">
          <p>
            Refund requests require us to reference the personal information you provided at registration (name, email,
            phone, order ID). We will communicate the status of your request by phone and email as described in our
            <Link to="/privacy" className="link-gold"> Privacy Policy</Link>. You are responsible for keeping the contact information on your account current.
          </p>
        </Section>

        <Section n={10} title="Governing law & dispute resolution">
          <p>
            This Refund Policy is governed by the laws of the United States of America and the State of Maryland, without
            regard to conflict-of-laws principles. Consumer-protection rights afforded to Maryland residents under
            <em> Md. Code, Com. Law § 13-101 et seq. (Maryland Consumer Protection Act)</em> are preserved.
          </p>
          <p>
            Any dispute arising from or relating to a purchase, refund, or this Policy shall be brought exclusively in the
            state or federal courts located in Prince George's County, Maryland, and you consent to personal jurisdiction
            and venue in those courts.
          </p>
        </Section>

        <Section n={11} title="Changes to this Policy">
          <p>
            SEP reserves the right to update this Policy at any time. The Policy version in effect on your date of purchase
            governs your transaction. The "Effective" date at the top of this page reflects the current version.
          </p>
        </Section>

        <div className="border-t border-white/10 py-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-neutral-500">
            Questions? · <a href="mailto:info@seprotection.com" className="link-gold">info@seprotection.com</a> · 410-645-0722
          </div>
          <Link to="/privacy" className="link-gold font-mono text-[10px] tracking-[0.2em] uppercase" data-testid="refund-privacy-link">
            → Privacy Policy
          </Link>
        </div>
      </div>
    </div>
  );
}
